/** @format */

import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { LI } from "../../../AbstractElements";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import axios from "axios";

const api_url = "https://t1mq0552b9.execute-api.us-east-1.amazonaws.com/dev";

// Supported Languages
const languages = [
  { code: "en-US", name: "English (US)" },
  { code: "es-ES", name: "Spanish (Spain)" },
  { code: "fr-FR", name: "French (France)" },
  { code: "de-DE", name: "German" },
  { code: "it-IT", name: "Italian" },
  { code: "zh-CN", name: "Chinese (Simplified)" },
  { code: "ar-SA", name: "Arabic" },
  { code: "ru-RU", name: "Russian" },
  { code: "ur-PK", name: "Urdu (Pakistan)" },
];

export default function AudioTranslator() {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [inputLang, setInputLang] = useState("en-US");
  const [outputLang, setOutputLang] = useState("es-ES"); // Default translation language
  const [uploadProgress, setUploadProgress] = useState(null);
  const [transcription, setTranscription] = useState("");
  const [translation, setTranslation] = useState("");
  const [jobName, setJobName] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  // 🎤 Start Recording
  const startRecording = async () => {
    setIsRecording(true);
    setUploadProgress(null);
    setTranscription("");
    setTranslation("");
    setUploadProgress(null);
    audioChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) =>
        audioChunksRef.current.push(event.data);
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/mp3",
        });
        setAudioBlob(audioBlob);
        uploadAudio(audioBlob);
      };

      mediaRecorder.start();
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  // ⏹ Stop Recording
  const stopRecording = () => {
    setIsRecording(false);
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  };

  // 📤 Upload Audio & Start Transcription
  const uploadAudio = async (audioBlob) => {
    const fileName = `tbo-audio-${Date.now()}.mp3`;
    const audioFile = new File([audioBlob], fileName, { type: "audio/mp3" });
    const formData = new FormData();
    formData.append("files[]", audioFile);

    setUploadProgress("Uploading audio...");
    try {
      const uploadResponse = await axios.post(
        `${api_url}/upload/audio`,
        formData
      );
      if (uploadResponse.data.code === 200) {
        setUploadProgress("Upload complete. Starting transcription...");
        startTranscription(fileName);
      } else {
        setUploadProgress("Upload failed.");
      }
    } catch (error) {
      console.error("Error uploading audio:", error);
      setUploadProgress("Error uploading audio.");
    }
  };

  // Start Transcription
  const startTranscription = async (fileName) => {
    try {
      const transcriptionResponse = await axios.post(
        `${api_url}/audio/translation`,
        {
          fileName,
          langCode: inputLang,
          targetLang: outputLang,
        }
      );
      if (transcriptionResponse.data.code === 200) {
        setJobName(transcriptionResponse.data.jobName);
        setUploadProgress(null);
      } else {
        setUploadProgress("Failed to initiate transcription.");
      }
    } catch (error) {
      console.error("Error starting transcription:", error);
      setUploadProgress("Error starting transcription.");
    }
  };

  // Fetch Translation
  const fetchTranslation = async () => {
    if (!jobName) return;
    setUploadProgress("Fetching translation...");
    try {
      const result = await axios.get(`${api_url}/audio/translation/${jobName}`);
      if (result.data.code === 200) {
        setTranscription(result.data.original_text);
        setTranslation(result.data.translated_text);
        setUploadProgress(null);
      } else {
        setUploadProgress("Translation not ready, try again shortly.");
      }
    } catch (error) {
      console.error("Error fetching translation:", error);
      setUploadProgress("Error retrieving translation. Try again shortly.");
    }
  };

  return (
    <LI className="" title="Audio Translator">
      <Link to="#" onClick={toggleModal}>
        <i className="fa fa-microphone"></i>
      </Link>
      <Modal
        isOpen={isModalOpen}
        toggle={toggleModal}
        className="audio-translator-modal"
      >
        <ModalHeader toggle={toggleModal}>
          <i className="fa fa-language"></i> Audio Translator
        </ModalHeader>
        <ModalBody>
          {/* 🎤 Language Selection */}
          <FormGroup>
            <Label for="inputLang">
              <i className="fa fa-microphone"></i> Select Input Language:
            </Label>
            <Input
              type="select"
              id="inputLang"
              value={inputLang}
              onChange={(e) => setInputLang(e.target.value)}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="outputLang">
              <i className="fa fa-language"></i> Select Output Language:
            </Label>
            <Input
              type="select"
              id="outputLang"
              value={outputLang}
              onChange={(e) => setOutputLang(e.target.value)}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </Input>
          </FormGroup>

          {/* 🎤 Recording Controls */}
          {isRecording ? (
            <Button color="danger" onClick={stopRecording} block>
              <i className="fa fa-stop"></i> Stop Recording
            </Button>
          ) : (
            <Button color="success" onClick={startRecording} block>
              <i className="fa fa-microphone"></i> Start Recording
            </Button>
          )}

          {/* ⏳ Progress */}
          {uploadProgress && (
            <div className="mt-3 text-center">
              <Spinner size="sm" color="primary" /> {uploadProgress}
            </div>
          )}

          {/* 🎯 Fetch Translation */}
          {jobName && !uploadProgress && (
            <div className="d-flex justify-content-between mt-3">
              <Button color="primary" onClick={fetchTranslation}>
                <i className="fa fa-refresh"></i> Get Translation
              </Button>
              <Button color="danger" onClick={() => setJobName(null)}>
                <i className="fa fa-times"></i> Cancel
              </Button>
            </div>
          )}

          {/* 📝 Display Results */}
          {transcription && (
            <div className="mt-3">
              <h5>
                <i className="fa fa-file-audio"></i> Transcription:
              </h5>
              <p>{transcription}</p>
            </div>
          )}
          {translation && (
            <div className="mt-3">
              <h5>
                <i className="fa fa-language"></i> Translation:
              </h5>
              <p>{translation}</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            <i className="fa fa-times"></i> Close
          </Button>
        </ModalFooter>
      </Modal>
    </LI>
  );
}
